import { h, Component } from 'preact';
import Store from 'store/store';

import { joinClasses, accessNested, commaSeparatedList } from 'utils/utils';
import { getShowtimesData } from 'utils/bolt/index';
import { expandFilterSelection, getMovieName, getFiltersOrderReference } from 'utils/bolt/showtimes';
import getCopyWithParams from 'utils/getCopyWithParams';
import formatDateTime from 'utils/formatDateTime';

import Button from 'components/bolt/button/button';
import Close from 'components/bolt/icons/close';

import s from 'components/bolt/showtimes/filterSelection2.sss';

export default class FilterSelection extends Component {
	constructor(props) {
		super(props);

		this.today = formatDateTime(new Date(), 'yyyy-MM-dd');

		this.onCancel = this.onCancel.bind(this);
		this.onOptionClick = this.onOptionClick.bind(this);
		this.onProviderOptionClick = this.onProviderOptionClick.bind(this);
	}

	onCancel() {
		this.props.onCancel?.();
		// Do not reset here, it will also get rid of filters that were there prior to opening this
		// Let the parent component clean up in the onCancel function
		// Store.emit('FILTERS_RESET');
	}

	onOptionClick(opt, filter) {
		if (opt.source === 'all') {
			Store.emit('FILTER_RESET_SINGLE', opt.filter || filter);
		} else {
			Store.emit('FILTER_TOGGLE', opt.filter || filter, opt.source);
		}
		Store.emit('FILTER_SCROLL_INTO_VIEW', opt.source);
	}

	onProviderOptionClick(opt) {
		// TODO: Store actions?
		if (opt.source === 'all') {
			Store.get().showtimes.remove('customDataProvider');
		} else {
			Store.get().showtimes.set('customDataProvider', opt.source);
		}
		Store.emit('SHOWTIMES_REFRESH');
	}

	isDefault() {
		let filters = this.props.filters;
		let selected = filters.selected;
		if (!selected) {
			return true;
		}
		let firstDate = filters.available && filters.available.date && filters.available.date[0];
		return Object.keys(selected).every(filter => {
			let sel = selected[filter];
			if (!sel || !sel.length) {
				return true;
			}
			return filter === 'date' && sel.length === 1 && (sel[0] === firstDate || sel[0] === this.today);
		});
	}

	getDisplayedFilters(available) {
		let allKeys = Object.keys(available);
		allKeys.sort((a, b) => {
			if (a === 'movie') return -1;
			if (b === 'movie') return 1;
			return 0;
		});

		// const showAudioDetailsAsFormat = Store.get().appData?.pages?.boltShowtimes?.data?.showAudioDetailsAsFormat;
		const selectedFormatsForPLatform = this.props.appData?.pages?.boltShowtimes?.data?.formatsAllowed;
		const list = allKeys.filter(e => e !== 'date' && e !== 'exhibitor' && available[e]?.length).map(f => {
			return { filter: f, mergeWith: f === 'audioDetails' && !selectedFormatsForPLatform?.length ? 'format' : null };
		});

		let providersFilter = commaSeparatedList(getShowtimesData()?.providers?.filterOptions);
		if (providersFilter?.length) {
			let userCountryCode = accessNested(this.props, 'client.geo.countryCode');
			const isUSRegion = userCountryCode === 'us';
			if (!isUSRegion && providersFilter.includes('fandango')) {
				providersFilter = providersFilter.filter(provider => provider !== 'fandango');
			}
			const current = Store.get().showtimes?.customDataProvider || [];
			list.push({
				filter: 'provider',
				options: providersFilter,
				allowSingle: true,
				trigger: this.onProviderOptionClick,
				getCurrentValue: () => current
			});
		}

		return list;
	}

	getFilterName(filter, option) {
		const filterCopy = this.props.appData.copy?.baseCopy?.filters?.[filter] || {};
		const options = filterCopy.useLangOptions ? this.props.appData.copy?.baseCopy?.languages : filterCopy;

		if (filter === 'movie') {
			return getMovieName(option);
		}

		return options[option] || option;
	}

	renderFilters(displayedFilter) {
		let { filters, appData } = this.props;
		const baseCopy = appData.copy?.baseCopy || {};
		const fontStyles = appData.styling.fontVariables;
		const accessibilityCopy = appData.copy.accessibilityCopy;
		const noFiltersCopy = baseCopy.$FILTERS_NONE_AVAILABLE || 'No filters available';

		const hideFilterOptionTime = getShowtimesData()?.hideFilterOptionTime;

		if (filters.loading || !filters.available) {
			return;
		}

		displayedFilter ||= this.getDisplayedFilters(filters.available);
		let filtersList = displayedFilter?.map(f => typeof f === 'string' ? { filter: f } : f)?.filter(e => e);
		if (!filtersList?.length) {
			return <div class={s.empty}>{noFiltersCopy}</div>;
		}

		let getSelected = (() => {
			let cache = {};
			return (filter) => cache[filter] || (cache[filter] = expandFilterSelection(filters.selected?.[filter] || [], filters.available?.[filter]));
		})();

		return filtersList.map((sourceData) => {
			let {
				filter,
				mergeWith,
				options,
				allowSingle,
				trigger = this.onOptionClick,
				getCurrentValue = getSelected,
			} = sourceData;

			if (mergeWith && filtersList.some(f => f.filter === mergeWith)) {
				return;
			}
			options ||= filters.available[filter];

			let merges = filtersList.filter(f => f?.mergeWith === filter);
			if (merges.length) {
				options = [filter, ...merges.map(m => m.filter)].flatMap(f => filters.available[f]?.map(opt => ({ value: opt, filter: f })));
			}

			let optionAriaLabel = '';
			if (filter === 'time') {
				if (hideFilterOptionTime) return;
				optionAriaLabel = accessibilityCopy.$TIME_SELECTION_ARIA;

				const order = ['morning', 'afternoon', 'evening', 'late'];
				options = order.filter(tod => options?.includes(tod));
			}

			if (!options.length || options.length < 1 || (options.length === 1 && !allowSingle && filter !== 'subtitles' && filter !== 'language')) {
				return;
			}

			let displayedOptions = Object.values(options.reduce((all, option) => {
				if (typeof option === 'string') {
					option = { value: option };
				}
				let currentFilter = option.filter || filter;
				let name = this.getFilterName(currentFilter, option.value);

				let active = getCurrentValue(currentFilter).includes(option.value);
				if (!all[name]) {
					all[name] = { name, active, source: [], filter: currentFilter };
				}
				all[name].source.push(option.value);
				all[name].active = all[name].active || active;
				return all;
			}, {}));

			if (filter === 'format') {
				optionAriaLabel = accessibilityCopy.$FORMAT_SELECTION_ARIA;
			}

			if (filter === 'format' || filter === 'movie') {
				displayedOptions = displayedOptions.sort((a, b) => {
					const orderingReference = getFiltersOrderReference(filter === 'format' ? 'format' : 'movieId');
					if (orderingReference.length) {
						const orderA = orderingReference.indexOf(a.source[0]);
						const orderB = orderingReference.indexOf(b.source[0]);
						if (orderA > -1 || orderB > -1) {
							return orderB - orderA;
						}
					}
					return a.name.localeCompare(b.name);
				});
			}

			const filterCopy = baseCopy.filters?.[filter];
			displayedOptions.unshift({
				active: getCurrentValue(filter).length === 0,
				name: getCopyWithParams(baseCopy, filterCopy?._all || baseCopy?.$ALL || 'All'),
				source: 'all'
			});

			const filterName = getCopyWithParams(baseCopy, filterCopy?._name || filter);

			return (
				<div class={s.filter} key={filter}>
					<div class={joinClasses(s.name, fontStyles.filterOverlayFilterTitle)}><span>{filterName}</span></div>
					<div class={s.options}>
						{displayedOptions.map(opt => (
							<Button
								key={opt.source === 'all' ? '--all' : opt.name}
								type="tag"
								class={joinClasses(s.option, opt.active && s.active, fontStyles.filterMenuFilter)}
								active={opt.active}
								trigger={() => trigger(opt, filter)}
								ariaLabel={optionAriaLabel.replace('<option>', opt.name)}
							>
								{opt.name}
							</Button>
						))}
					</div>
				</div>
			);
		});
	}

	render(props) {
		const { open, appData, filters } = props;
		const filtersCopy = appData.copy?.baseCopy?.$FILTERS || 'Filters';
		const closeCopy = appData.copy?.baseCopy?.$FILTERS_CLOSE || 'Close';
		const accessibilityCopy = appData.copy.accessibilityCopy || {};
		const fontStyles = appData.styling?.fontVariables;
		let submitCopy = appData.copy?.baseCopy?.$VIEW_SHOWTIMES || 'View Showtimes';
		const isBareboneShowtimesView = accessNested(Store.get(), 'appData.options.bareboneShowtimesView', false);

		const displayedFilter = this.getDisplayedFilters(filters.available);
		if (!filters.loading && !displayedFilter?.length) {
			submitCopy = closeCopy;
		}

		return (
			<div class={joinClasses(s.wrapper, isBareboneShowtimesView && s.barebone, open && s.open)}>
				<div class={s.shadow} onClick={this.onCancel} />
				<div class={s.container}>
					<div class={s.scroller}>
						<div class={s.content}>
							<div class={s.header}>
								<h4 class={joinClasses(s.title, fontStyles.filterOverlayTitle)}>{filtersCopy}</h4>
								<Close class={s.close} onClick={this.onCancel} />
							</div>
							<div class={s.filters}>
								{this.renderFilters(displayedFilter)}
							</div>
							<div class={s.footer}>
								<Button
									type="primary"
									class={joinClasses(s.submit, fontStyles.filterMenuViewShowtimes)}
									trigger={props.onSubmit}
									ariaLabel={accessibilityCopy.$VIEW_SHOWTIMES_ARIA}
								>
									{submitCopy}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
